import { Badge, Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { useState } from "react";
import { VenueSelector } from "../StagingGroupCard/VenueSelector";
import { AttendeesList } from "./AttendeesList";
import { EventTestClockPanel } from "./EventTestClockPanel";
import { ExistingEventCardProps, formatTime } from "./types";

export const ExistingEventCard = ({
  event,
  venues,
  isSelected,
  onSelect,
  onRemoveStaged,
  onSave,
  onSaveVenue,
  onConfirm,
  onChangeRequest,
  onCancel,
}: ExistingEventCardProps) => {
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<number | null>(
    event.restaurantId ?? null
  );
  const [selectedAfterPartyId, setSelectedAfterPartyId] = useState<number | null>(
    event.afterPartyVenueId ?? null
  );

  const activeAttendees = event.reservationRequests.filter(
    (req) => req.status !== "CANCELLED" && req.status !== "CANCELLED_NO_REFUND"
  );
  const totalAttendees = activeAttendees.length + event.stagedRequests.length;
  const canConfirm = event.stagedRequests.length > 0;

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      onClick={onSelect}
      style={{
        cursor: "pointer",
        borderColor: isSelected ? "blue" : undefined,
        borderWidth: isSelected ? "2px" : "1px",
        resize: "vertical",
        overflow: "auto",
        minHeight: "400px",
      }}
    >
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={4} c="black">
              {formatTime(event.startTime)}
            </Title>
            <Text c="gray" size="sm">
              {event.locationName}
            </Text>
          </div>
          <Badge size="lg" color="blue">
            {totalAttendees} / {event.capacity}명
          </Badge>
        </Group>

        <div onClick={(e) => e.stopPropagation()}>
          <EventTestClockPanel
            eventId={event.id}
            testClock={event.testClock}
            attendees={activeAttendees}
          />
        </div>

        {/* Group-level inputs */}
        <div onClick={(e) => e.stopPropagation()}>
          <Stack gap="xs">
            <VenueSelector
              label="레스토랑"
              placeholder="레스토랑 선택 (선택사항)"
              selectedVenueId={selectedRestaurantId}
              venues={venues}
              venueType="RESTAURANT"
              onChange={setSelectedRestaurantId}
            />
            <VenueSelector
              label="애프터파티"
              placeholder="애프터파티 선택 (선택사항)"
              selectedVenueId={selectedAfterPartyId}
              venues={venues}
              venueType="AFTER_PARTY"
              onChange={setSelectedAfterPartyId}
            />
          </Stack>
        </div>

        {/* Attendees Section */}
        <div>
          <Text c="black" size="sm" fw={700} mb="xs">
            참석자 ({activeAttendees.length}명)
            {event.stagedRequests.length > 0 && (
              <Text component="span" c="orange" ml="xs">
                + {event.stagedRequests.length}명 Staging
              </Text>
            )}
          </Text>
          <div onClick={(e) => e.stopPropagation()}>
            <AttendeesList
              attendees={event.reservationRequests}
              stagedRequests={event.stagedRequests}
              eventStartTime={event.startTime}
              eventLocationName={event.locationName}
              onRemoveStaged={onRemoveStaged}
              onChangeRequest={onChangeRequest}
              onCancel={onCancel}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <Group justify="flex-end" gap="sm">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onSave();
            }}
          >
            저장
          </Button>
          <Button
            variant="outline"
            color="blue"
            onClick={(e) => {
              e.stopPropagation();
              onSaveVenue(selectedRestaurantId, selectedAfterPartyId);
            }}
          >
            베뉴 저장
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onConfirm();
            }}
            disabled={!canConfirm}
          >
            이벤트 확정
          </Button>
        </Group>
      </Stack>
    </Card>
  );
};
